import { menuRegistry } from './menuRegistry';
import type {
  LangMap,
  LegacyMenuItem,
  LicenseTypeState,
  MenuFamily,
  StoreState,
  VisibleMenuItem,
  VisibleTabItem,
} from './types';

const ACTIVE_MAIN_COLOR = {
  bgcolor: '#000',
  color: '#fff',
};

const INACTIVE_MAIN_COLOR = {
  bgcolor: '#ffffff',
  color: '#000000',
};

const LOGOUT_MAIN_COLOR = {
  bgcolor: '#FF0000',
  color: '#ffffff',
  border: '#FF0000',
};

const DEFAULT_MENU_KEY = 'erpsam';
const TECHNICIAN_WORK_ONLY_ROLE = 'maintenance_technician_work_only';
const TECHNICIAN_WORKBENCH_ROUTE = '/technician/job-items';
const BASELINE_GROUP_PERMISSIONS = ['maintenance.rentals.*'];

// Screens a user group may always open, even when the employee's role denies the wider
// permission. Mirrors GROUP_REQUEST_GRANTS in php/includes/classes/class.EmployeePermissions.php.
const GROUP_ROUTE_GRANTS: Record<string, string[]> = {
  // Supervisor: job card -> invoice, new items, daily sales report.
  '6': ['/inventory/products', '/salesreport'],
  // Salesman: every job card, and following it up.
  '5': ['/home', '/view-card', '/print-card'],
};

function hasGroupRouteGrant(routePath = '', state: StoreState = {}): boolean {
  const grants = GROUP_ROUTE_GRANTS[normalizeId(state.group_id)] || [];
  const normalizedRoute = (routePath.split('?')[0].replace(/\/+$/, '') || '/').toLowerCase();
  return grants.some((route) => normalizedRoute === route || normalizedRoute.startsWith(`${route}/`));
}

const MENU_KEY_ALIASES: Record<string, string> = {
  bill: DEFAULT_MENU_KEY,
};

const ACCESS_ROUTE_RULES: Array<{ path?: string; prefix?: string; permissions: string[] }> = [
  { path: '/ecar', permissions: ['reports.read'] },
  { path: '/dashboard', permissions: ['reports.read'] },
  { path: '/new-dashboard', permissions: ['reports.read'] },
  { prefix: '/dashboard', permissions: ['reports.read'] },
  { path: '/technician/job-items', permissions: ['maintenance.technician.work'] },
  { path: '/reports/technician-work', permissions: ['maintenance.reports.work'] },
  { path: '/device-rentals', permissions: ['maintenance.rentals.read'] },
  { path: '/maintenance-ratings', permissions: ['maintenance.reports.work'] },
  { path: '/home', permissions: ['maintenance.*'] },
  { path: '/workreps', permissions: ['maintenance.reports.work'] },
  { path: '/supervisor-report', permissions: ['maintenance.reports.work'] },
  { path: '/reports/percent-report', permissions: ['maintenance.reports.work'] },
  { path: '/reports/percentage-reports', permissions: ['maintenance.reports.work'] },
  { path: '/percentage-reports', permissions: ['maintenance.reports.work'] },
  { path: '/card-profit-reports', permissions: ['maintenance.reports.profit'] },
  { path: '/work-orders', permissions: ['maintenance.work_orders.read'] },
  { path: '/emps/workorders', permissions: ['maintenance.work_orders.read'] },
  { prefix: '/general-maintenance', permissions: ['maintenance.*'] },
  { prefix: '/management/fixed-assets', permissions: ['accounting.read'] },
  { prefix: '/maintcontracts', permissions: ['maintenance.*'] },
  { prefix: '/maint-contract', permissions: ['maintenance.*'] },
  { prefix: '/external-maint', permissions: ['maintenance.*'] },
  { prefix: '/periodic-maint', permissions: ['maintenance.*'] },
  { prefix: '/checkupcar', permissions: ['maintenance.*'] },
  { prefix: '/checkupdevice', permissions: ['maintenance.*'] },
  { prefix: '/cars-cards', permissions: ['maintenance.*'] },
  { prefix: '/view-card', permissions: ['maintenance.*'] },
  { prefix: '/print-card', permissions: ['maintenance.*'] },
  { prefix: '/signcard', permissions: ['maintenance.*'] },

  { prefix: '/settings', permissions: ['settings.manage'] },
  { prefix: '/zatca-phase-2', permissions: ['settings.manage'] },
  { prefix: '/items-types', permissions: ['settings.manage'] },
  { prefix: '/settings-whatsapp', permissions: ['settings.manage'] },
  { prefix: '/emps/users-settings', permissions: ['settings.manage'] },

  { prefix: '/newPurchase', permissions: ['purchases.manage'] },
  { prefix: '/mg-purchase-report', permissions: ['purchases.read'] },
  { prefix: '/return-purchase', permissions: ['purchases.manage'] },
  { prefix: '/purchaseReturn', permissions: ['purchases.manage'] },
  { prefix: '/view-purchase', permissions: ['purchases.read'] },
  { prefix: '/pos/Purchase', permissions: ['purchases.read'] },
  { prefix: '/general-expenses', permissions: ['purchases.read'] },
  { prefix: '/purchase', permissions: ['purchases.read'] },

  { prefix: '/stores-transfer', permissions: ['inventory.warehouses.manage'] },
  { prefix: '/start-inventory', permissions: ['inventory.warehouses.manage'] },
  { prefix: '/store-settlement', permissions: ['inventory.warehouses.manage'] },
  { prefix: '/stores', permissions: ['inventory.warehouses.read'] },
  { prefix: '/inventory-reports', permissions: ['inventory.warehouses.read'] },
  { prefix: '/inventory-history', permissions: ['inventory.warehouses.read'] },
  { prefix: '/inventory/itemtrans', permissions: ['inventory.warehouses.read'] },

  { prefix: '/finance', permissions: ['accounting.read'] },
  { prefix: '/coa-reports', permissions: ['accounting.read', 'reports.read'] },
  { prefix: '/reports/balance', permissions: ['accounting.read', 'reports.read'] },
  { prefix: '/reports/acbalance', permissions: ['accounting.read', 'reports.read'] },
  { prefix: '/reports/bnl', permissions: ['accounting.read', 'reports.read'] },
  { prefix: '/reports/statment', permissions: ['accounting.read', 'reports.read'] },
  { prefix: '/reports/vat', permissions: ['accounting.read', 'reports.read'] },
  { prefix: '/daily-report', permissions: ['reports.read'] },
  { path: '/reports/salesman-report', permissions: ['sales.read'] },
  { prefix: '/reports', permissions: ['reports.read'] },

  { prefix: '/orders', permissions: ['sales.read'] },
  { prefix: '/quotations', permissions: ['sales.read'] },
  { prefix: '/view-quot', permissions: ['sales.read'] },
  { prefix: '/invoicelist', permissions: ['sales.read'] },
  { prefix: '/returnlist', permissions: ['sales.read'] },
  { prefix: '/salesreport', permissions: ['sales.read', 'reports.read'] },
  { prefix: '/customers', permissions: ['customers.read'] },
  { prefix: '/clientlist', permissions: ['customers.read'] },

  { prefix: '/emps/salaries', permissions: ['management.employees.read'] },
  { prefix: '/emps/salary-table', permissions: ['management.employees.read'] },
  { prefix: '/emps/custody', permissions: ['management.employees.read'] },
  { prefix: '/emps/attendees', permissions: ['management.employees.read'] },
  { prefix: '/emps/gozi-report', permissions: ['management.employees.read', 'reports.read'] },
  { prefix: '/emp/list', permissions: ['management.employees.read'] },
  { prefix: '/updateuser', permissions: ['settings.manage'] },
];

function safeParseJson(value: string | null): unknown {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
}

function normalizeId(value: number | string | undefined | null): string {
  return String(value ?? '');
}

function getStoredLicense(): LicenseTypeState | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const value = safeParseJson(localStorage.getItem('_ECar_'));
  return value && typeof value === 'object' ? value as LicenseTypeState : null;
}

function getStoredCurrentMain(): string {
  if (typeof localStorage === 'undefined') {
    return '';
  }

  return localStorage.getItem('currentMenu') || '';
}

export function isKnownMenuKey(menuKey: string | undefined | null): boolean {
  return Boolean(menuKey && menuRegistry[menuKey]);
}

export function resolveMenuKey(menuKey: string | undefined | null): string {
  if (isKnownMenuKey(menuKey)) {
    return String(menuKey);
  }

  const alias = menuKey ? MENU_KEY_ALIASES[menuKey] : '';
  if (isKnownMenuKey(alias)) {
    return alias;
  }

  return DEFAULT_MENU_KEY;
}

export function getActiveMenuKey(state: StoreState = {}): string {
  const currentState = state || {};
  const storedLicense = getStoredLicense();
  return resolveMenuKey(
    (storedLicense && storedLicense.activemenu) ||
    (currentState.licenseType && currentState.licenseType.activemenu) ||
    currentState.activemenu ||
    ''
  );
}

export function getMenuFamily(state: StoreState = {}, menuKey = getActiveMenuKey(state)): MenuFamily {
  const resolvedMenuKey = resolveMenuKey(menuKey);
  if (!state || !resolvedMenuKey || !menuRegistry[resolvedMenuKey]) {
    return {};
  }

  return menuRegistry[resolvedMenuKey];
}

export function resolveMenuLabel(item: LegacyMenuItem, lang: LangMap = {}): string {
  if (!item) return '';

  if (item.labelKey && lang && lang[item.labelKey]) {
    return String(lang[item.labelKey]);
  }

  if (lang && lang.langname === 'ur') {
    return item.urduname || item.urname || item.enname || item.arname || '';
  }

  if (lang && lang.langname === 'en') {
    return item.enname || item.arname || item.urduname || item.urname || '';
  }

  return item.arname || item.enname || item.urduname || item.urname || '';
}

export function hasGroupAccess(item: LegacyMenuItem, groupId: number | string | undefined): boolean {
  if (!item || !Array.isArray(item.groupid) || item.groupid.length === 0) {
    return true;
  }

  if (groupId === undefined || groupId === null || normalizeId(groupId) === '' || normalizeId(groupId) === '0') {
    return true;
  }

  return item.groupid.some((id) => normalizeId(id) === normalizeId(groupId));
}

export function hasPermissionAccess(item: LegacyMenuItem, licenseType: LicenseTypeState): boolean {
  if (!item || !Array.isArray(item.perms) || item.perms.length === 0) {
    return true;
  }

  return item.perms.some((permission) => permission === 'any' || Boolean(licenseType && licenseType[permission]));
}

function normalizePermissionList(value: string[] | Record<string, unknown> | undefined): string[] {
  if (Array.isArray(value)) {
    return value.map((permission) => String(permission || '').trim()).filter(Boolean);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value).filter((permission) => Boolean(value[permission]));
  }

  return [];
}

function isTechnicianWorkOnlyState(state: StoreState = {}): boolean {
  return normalizePermissionList(state.roleKeys).includes(TECHNICIAN_WORK_ONLY_ROLE);
}

function permissionMatches(pattern: string, permission: string): boolean {
  if (!pattern || !permission) return false;
  if (pattern === '*') return true;
  if (pattern === permission) return true;

  if (pattern.endsWith('.*')) {
    return permission.startsWith(pattern.slice(0, -1));
  }

  return false;
}

function hasExplicitPermission(state: StoreState = {}, permission: string): boolean {
  if (!permission) return true;

  const mode = String(state.permissionMode || 'legacy');
  if (mode === 'loading') {
    return false;
  }

  if (mode === 'admin') {
    return true;
  }

  if (mode === 'legacy') {
    return true;
  }

  const deniedPermissions = normalizePermissionList(state.deniedPermissions);
  if (deniedPermissions.some((denied) => permissionMatches(denied, permission))) {
    return false;
  }

  const groupId = normalizeId(state.group_id);
  if (groupId && groupId !== '0' && BASELINE_GROUP_PERMISSIONS.some((allowed) => permissionMatches(allowed, permission))) {
    return true;
  }

  const allowedPermissions = normalizePermissionList(state.userPermissions);
  return allowedPermissions.some((allowed) => permissionMatches(allowed, permission));
}

export function getRequiredPermissionsForRoute(routePath = ''): string[] {
  if (!routePath) return [];

  const normalizedRoute = (routePath.split('?')[0].replace(/\/+$/, '') || '/').toLowerCase();
  const matched = ACCESS_ROUTE_RULES.find((rule) => {
    if (rule.path && normalizedRoute === rule.path.toLowerCase()) return true;
    return Boolean(rule.prefix && normalizedRoute.startsWith(rule.prefix.toLowerCase()));
  });

  return matched ? matched.permissions : [];
}

export function hasExplicitRouteAccess(routePath = '', state: StoreState = {}): boolean {
  if (isTechnicianWorkOnlyState(state)) {
    const normalizedRoute = (routePath.split('?')[0].replace(/\/+$/, '') || '/').toLowerCase();
    return ['/', '/login', '/logout', TECHNICIAN_WORKBENCH_ROUTE].includes(normalizedRoute);
  }

  const requiredPermissions = getRequiredPermissionsForRoute(routePath);
  if (!requiredPermissions.length || hasGroupRouteGrant(routePath, state)) {
    return true;
  }

  return requiredPermissions.every((permission) => hasExplicitPermission(state, permission));
}

function normalizeRouteValue(item: LegacyMenuItem | undefined): string {
  if (!item) return '';
  return item.href || item.to || '';
}

function normalizeRoutePath(routePath = ''): string {
  return routePath ? (routePath.split('?')[0].replace(/\/+$/, '') || '/').toLowerCase() : '';
}

function isTechnicianWorkbenchItem(item: LegacyMenuItem): boolean {
  const route = normalizeRoutePath(normalizeRouteValue(item));
  const accessPerms = Array.isArray(item.accessPerms) ? item.accessPerms : [];
  return route === TECHNICIAN_WORKBENCH_ROUTE
    || accessPerms.includes('maintenance.technician.work');
}

function isLogoutItem(item: LegacyMenuItem): boolean {
  const route = normalizeRoutePath(normalizeRouteValue(item));
  return route === '/logout' || String(item.enname || '').toLowerCase() === 'logout';
}

export function getDefaultAuthorizedRoute(state: StoreState = {}, preferredRoute = ''): string {
  if (isTechnicianWorkOnlyState(state)) {
    return TECHNICIAN_WORKBENCH_ROUTE;
  }

  const normalizedPreferred = preferredRoute ? (preferredRoute.charAt(0) === '/' ? preferredRoute : `/${preferredRoute}`) : '';

  if (normalizedPreferred && hasExplicitRouteAccess(normalizedPreferred, state)) {
    return normalizedPreferred;
  }

  const currentMenu = getMenuFamily(state);
  const candidateRoutes: string[] = [];

  Object.values(currentMenu).forEach((section) => {
    if (!section) return;

    const tabs = Array.isArray(section.sub) ? section.sub || [] : [];
    tabs.forEach((tab) => {
      const route = normalizeRouteValue(tab);
      if (route && canShowMenuItem(tab, state)) {
        candidateRoutes.push(route);
      }
    });

    const mainRoute = normalizeRouteValue(section.main);
    if (mainRoute && section.main && canShowMenuItem(section.main, state)) {
      candidateRoutes.push(mainRoute);
    }
  });

  const fallbackRoutes = [
    '/technician/job-items',
    '/home',
    '/ecar',
    '/workreps',
    '/reports/technician-work',
    '/clientList',
    '/purchase',
    '/finance',
    '/emp/list',
    '/settings',
  ];

  const candidates = candidateRoutes.concat(fallbackRoutes);
  const uniqueCandidates = candidates.filter((route, index) => route && candidates.indexOf(route) === index);
  const found = uniqueCandidates.find((route) => hasExplicitRouteAccess(route, state));

  return found || '/ecar';
}

export function hasExplicitMenuAccess(item: LegacyMenuItem, state: StoreState = {}): boolean {
  if (!item) return false;

  const explicitPermissions = Array.isArray(item.accessPerms) ? item.accessPerms : [];
  const routePermissions = getRequiredPermissionsForRoute(item.href || item.to || '');
  const requiredPermissions = explicitPermissions.length ? explicitPermissions : routePermissions;

  if (!requiredPermissions.length || hasGroupRouteGrant(item.href || item.to || '', state)) {
    return true;
  }

  return requiredPermissions.every((permission) => hasExplicitPermission(state, permission));
}

export function canShowMenuItem(item: LegacyMenuItem, state: StoreState = {}): boolean {
  if (!item) return false;

  const currentState = state || {};
  if (isTechnicianWorkOnlyState(currentState)) {
    return (isTechnicianWorkbenchItem(item) || isLogoutItem(item))
      && hasExplicitMenuAccess(item, currentState);
  }

  return hasGroupAccess(item, currentState.group_id)
    && hasPermissionAccess(item, currentState.licenseType || {})
    && hasExplicitMenuAccess(item, currentState);
}

export function findMainIndexByRoute(state: StoreState = {}, routePath: string): string {
  if (!routePath) return '';

  const currentMenu = getMenuFamily(state);
  const sections = Object.values(currentMenu);

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    const main = section && section.main;
    const tabs = Array.isArray(section && section.sub) ? section.sub || [] : [];

    if (!main) continue;

    if (main.href === routePath || main.to === routePath || tabs.some((tab) => tab.href === routePath || tab.to === routePath)) {
      return normalizeId(main.index);
    }
  }

  return '';
}

export function getActiveMainIndex(state: StoreState = {}, routePath = ''): string {
  return findMainIndexByRoute(state, routePath) || getStoredCurrentMain();
}

export function getVisibleMainMenu(state: StoreState = {}, lang: LangMap = {}, routePath = ''): VisibleMenuItem[] {
  const currentMenu = getMenuFamily(state);
  const activeMainIndex = getActiveMainIndex(state, routePath);
  const result: VisibleMenuItem[] = [];

  Object.values(currentMenu).forEach((section) => {
    const main = section && section.main;
    if (!main) {
      return;
    }

    const tabs = Array.isArray(section && section.sub) ? section.sub || [] : [];
    const visibleTabs = tabs.filter((tab) => canShowMenuItem(tab, state));
    const canOpenMain = canShowMenuItem(main, state);
    const firstVisibleTab = visibleTabs.find((tab) => normalizeRouteValue(tab));

    if (!canOpenMain && !firstVisibleTab) {
      return;
    }

    const isLogout = main.enname === 'Logout';
    const isTraining = main.enname === 'training';
    const isActive = activeMainIndex === normalizeId(main.index);
    const resolvedRoute = canOpenMain ? normalizeRouteValue(main) : normalizeRouteValue(firstVisibleTab);
    const colors: { bgcolor: string; color: string; border?: string } = isLogout
      ? LOGOUT_MAIN_COLOR
      : isActive
        ? ACTIVE_MAIN_COLOR
        : INACTIVE_MAIN_COLOR;

    result.push({
      ...main,
      href: resolvedRoute || main.href,
      to: resolvedRoute || main.to,
      text: resolveMenuLabel(main, lang),
      bgcolor: colors.bgcolor,
      color: colors.color,
      border: colors.border || main.border || '#000',
      frame: isLogout ? 'logout' : isTraining ? 'iframe' : main.frame || '',
    });
  });

  return result;
}

export function getTabsForMain(state: StoreState = {}, mainIndex: number | string): LegacyMenuItem[] {
  const currentMenu = getMenuFamily(state);

  for (const section of Object.values(currentMenu)) {
    if (normalizeId(section && section.main && section.main.index) === normalizeId(mainIndex)) {
      return Array.isArray(section.sub) ? section.sub || [] : [];
    }
  }

  return [];
}

function toVisibleTabs(state: StoreState = {}, lang: LangMap = {}, routePath = '', tabs: LegacyMenuItem[] = []): VisibleTabItem[] {
  return tabs
    .filter((tab) => canShowMenuItem(tab, state))
    .map((tab) => ({
      ...tab,
      class: routePath === tab.href ? 'mytab' : tab.class === 'mytab' ? 'mytab4' : tab.class || 'mytab4',
      name: resolveMenuLabel(tab, lang),
      style: tab.style || '',
      smclass: tab.smclass || '',
    }));
}

export function getVisibleTabsForMain(state: StoreState = {}, lang: LangMap = {}, mainIndex: number | string = '', routePath = ''): VisibleTabItem[] {
  try {
    return toVisibleTabs(state, lang, routePath, getTabsForMain(state, mainIndex));
  } catch (_error) {
    return [];
  }
}

export function getVisibleTabsForMatchedRoute(state: StoreState = {}, lang: LangMap = {}, routePath = ''): VisibleTabItem[] {
  const matchedMainIndex = findMainIndexByRoute(state, routePath);
  if (!matchedMainIndex) {
    return [];
  }

  return getVisibleTabsForMain(state, lang, matchedMainIndex, routePath);
}

export function getVisibleTabsForRoute(state: StoreState = {}, lang: LangMap = {}, routePath = ''): VisibleTabItem[] {
  try {
    return getVisibleTabsForMain(state, lang, getActiveMainIndex(state, routePath), routePath);
  } catch (_error) {
    return [];
  }
}
